// User-marked POIs — vom Rider per Tap markierte Punkte (Trinkwasser, Café,
// Pranzo, Bike-Shop, Strand …) am aktuellen GPS-Fix.
//
// Wave 14cf-fix3 — User-Wunsch: "POI markieren" als 5. Aktion im PLUS-Menü.
//
// Architektur-Entscheidung (vorerst device-local):
//   - Eine IDB-Row pro Marker, Store `userPois` (s. db.ts v9).
//   - Kein Server-Sync. Der User behält seine markierten Punkte auf dem
//     Gerät; wenn er Bedarf hat sie zu teilen, kommt das in einer
//     späteren Wave (analog zu cruxQueue, evtl. mit "share with friends"-
//     Endpoint).
//   - Categories-Registry hier zentral. Aktuell nur `drinking_water` aktiv;
//     `cafe` / `pranzo` / `bike_shop` / `beach` sind als "coming soon"-
//     Platzhalter im Picker sichtbar, aber nicht klickbar — wenn sie
//     freigeschaltet werden, einfach `enabled: true` setzen.
//
// API:
//   markUserPoi({ lat, lon, category, ele?, note? }) → { id }
//   listUserPois() → readonly Array, sortiert "neueste zuerst"
//   deleteUserPoi(id)
//   USER_POI_CATEGORIES — UI-Registry mit Label/Emoji/enabled-Flag

import { withDb } from './storage/db';

// ─── Categories-Registry ────────────────────────────────────────────────

export interface UserPoiCategory {
  id: string;
  label: string;
  emoji: string;
  /**
   * Wenn `false`: im Category-Picker als deaktiviert/„coming soon" anzeigen.
   * Wenn `true`: User kann tatsächlich markieren.
   *
   * Reihenfolge im Picker entspricht der Reihenfolge in diesem Array.
   */
  enabled: boolean;
}

export const USER_POI_CATEGORIES: readonly UserPoiCategory[] = [
  { id: 'drinking_water', label: 'Drinking water', emoji: '💧', enabled: true },
  { id: 'cafe',           label: 'Café',           emoji: '☕', enabled: false },
  { id: 'pranzo',         label: 'Lunch & dinner', emoji: '🍝', enabled: false },
  { id: 'bike_shop',      label: 'Bike shop',      emoji: '🔧', enabled: false },
  { id: 'beach',          label: 'Beach',          emoji: '🏖️', enabled: false },
  { id: 'viewpoint',      label: 'Viewpoint',      emoji: '🏞️', enabled: false },
];

/** Helper: ist die gegebene Kategorie gerade aktiv? */
export function isUserPoiCategoryEnabled(id: string): boolean {
  return USER_POI_CATEGORIES.some((c) => c.id === id && c.enabled);
}

// ─── Storage-API ────────────────────────────────────────────────────────

export interface UserPoi {
  id: string;
  category: string;
  lat: number;
  lon: number;
  ele?: number | null;
  note?: string | null;
  createdAt: string;
}

/**
 * Generiert eine neue, eindeutige User-POI-ID. Format:
 *   `${category}:${timestamp}-${randomSlug}`
 *
 * Timestamp + Random verhindern Kollisionen wenn der User in derselben
 * Millisekunde zwei POIs markiert (extrem unwahrscheinlich, aber billig
 * abzusichern).
 */
function newId(category: string): string {
  const t = Date.now();
  const r = Math.random().toString(36).slice(2, 8);
  return `${category}:${t}-${r}`;
}

export interface MarkUserPoiInput {
  lat: number;
  lon: number;
  category: string;
  ele?: number | null;
  note?: string | null;
}

/**
 * Markiert einen neuen User-POI an der gegebenen Position. Wirft wenn
 * die Kategorie nicht aktiviert ist (defensive — der Caller sollte das
 * über USER_POI_CATEGORIES.enabled vorab prüfen, aber die DB sieht
 * keinen Grund unaktive Kategorien zuzulassen).
 */
export async function markUserPoi(
  input: MarkUserPoiInput,
): Promise<{ id: string }> {
  if (!isUserPoiCategoryEnabled(input.category)) {
    throw new Error(`POI category "${input.category}" is not available (yet).`);
  }
  if (!Number.isFinite(input.lat) || !Number.isFinite(input.lon)) {
    throw new Error('Invalid coordinates — no GPS fix?');
  }
  const id = newId(input.category);
  const row: UserPoi = {
    id,
    category: input.category,
    lat: input.lat,
    lon: input.lon,
    ele:
      typeof input.ele === 'number' && Number.isFinite(input.ele)
        ? input.ele
        : null,
    note: input.note?.trim() ? input.note.trim() : null,
    createdAt: new Date().toISOString(),
  };
  await withDb(async (db) => {
    await db.put('userPois', row);
  });
  return { id };
}

/** Liest alle User-POIs, sortiert "neueste zuerst". */
export async function listUserPois(): Promise<UserPoi[]> {
  return withDb(async (db) => {
    const all = (await db.getAll('userPois')) as UserPoi[];
    // by-created-Index existiert, aber db.getAllFromIndex liefert
    // aufsteigend — wir wollen absteigend (neueste oben), daher hier
    // einmal in JS umdrehen.
    all.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
    return all;
  });
}

/** Löscht einen User-POI. Wirft nicht wenn die ID nicht existiert. */
export async function deleteUserPoi(id: string): Promise<void> {
  await withDb(async (db) => {
    await db.delete('userPois', id);
  });
}
